import React, { useState } from 'react';
import { translate } from 'translations/i18n';
import cx from 'classnames';
import { Avatar } from '@companycam/slab-web';
import styled, { keyframes } from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Steven } from 'helpers/steven';

import { useModal } from 'components/shared/hooks';
import { splitTokenizedText } from './helpers/tokenutils';
import { useCurrentCompany, useAvatarData } from 'javascripts/hooks';

import { useCreateCommentTranslation } from 'data/v3/dynamicTranslations';
import CommentTranslateButton from './CommentTranslateButton';
import {
  LD_DYNAMIC_TRANSLATION,
  LD_SET_A_REMINDER_FLAG,
} from 'constants/featureFlags';
import { useDynamicTranslationsProviderName } from './useDynamicTranslationsProviderName';
import { ReminderButton } from './reminders/ReminderButton';
import { formatCompactDateWithTime, locale } from 'helpers/intl-helper';

export type CommentType = {
  __typename: string;
  id: string;
  databaseId: string;
  comment: string;
  contributor: {
    name: string;
    __typename: string;
    companyId: string;
    companyName: string;
  };
  createdAt: string;
  metadata?: {
    mentions: Array<{ id: string }>;
    users: Array<{
      id: string;
    }>;
  };
  commentable: {
    __typename: string;
    id: string;
  };
};

type CommentProps = {
  isCurrentUser: boolean;
  comment: CommentType;
  originalLanguage?: string[];
};

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
`;

const Loader = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  border-radius: var(--cc_size_border_radius_xl);
  border-top-left-radius: 0;
  background-color: var(--cc_color_background_3);
  animation: ${pulse} 1.5s infinite;
`;

const CommentWrapper = styled.div<{ isPending: boolean }>`
  position: relative;

  .commentor-comment {
    transition: opacity 0.3s;
    opacity: ${(props) => (props.isPending ? 0 : 1)};
  }
`;

const StyledButtonContainer = styled.div`
  position: absolute;
  top: calc(var(--cc_size_spacing_m) * -1);
  right: var(--cc_size_spacing_m);
  z-index: 1000;
`;

const StyledWrapper = styled.div<{ isEnabled: boolean }>`
  position: relative;

  ${(props) =>
    props.isEnabled
      ? `
      .comment-reminder,
      .comment-reminder * {
        visibility: hidden;
      }

      &:hover {
        background-color: var(--cc_color_background_3);
        .comment-reminder,
        .comment-reminder * {
          visibility: visible;
      }
  }`
      : `
      .comment-reminder,
      .comment-reminder * {
        display:none;
      }`}
`;

const boldableText = ({
  key,
  text,
  isBold,
}: {
  key: string;
  text: string;
  isBold: boolean;
}) => (isBold ? <b key={key}>{text}</b> : <span key={key}>{text}</span>);

const getFormattedComment = ({ id, comment, metadata }: CommentType) => {
  const tokens = metadata ? metadata.mentions || metadata.users || [] : [];

  if (tokens.length === 0) {
    return boldableText({
      key: `${id}-1`,
      text: comment,
      isBold: false,
    });
  }

  return splitTokenizedText(comment, tokens).map((chunk) =>
    boldableText({
      key: `${id}-${chunk.start}`,
      text: chunk.text,
      isBold: !!chunk.value,
    }),
  );
};

const Comment = ({
  isCurrentUser,
  comment,
  originalLanguage,
}: CommentProps) => {
  const { contributor } = comment;
  const avatarData = useAvatarData(comment);
  const { currentCompany } = useCurrentCompany();
  const { showModal, hideModal, Modal } = useModal();
  const isGuest = contributor.__typename === 'ProjectContributingGuest';
  const isDifferentCompany = currentCompany.node_id !== contributor.companyId;
  const commentClasses = cx('single-comment', {
    'is-current-user': isCurrentUser,
    'external-user': isGuest,
  });
  const formattedComment = getFormattedComment(comment);
  let creatorDisplayName = contributor.name || 'Unknown User';
  if (isDifferentCompany && contributor.companyName) {
    creatorDisplayName = `${creatorDisplayName} (${contributor.companyName})`;
  }

  // Translations beta: To be removed eventually
  const flags = useFlags();
  const translationServices = [
    'gpt-4o-mini',
    'amazon.nova-lite-v1:0',
    'anthropic.claude-3-sonnet-20240229-v1:0',
  ];
  const isDynamicTranslationEnabled = translationServices.includes(
    flags[LD_DYNAMIC_TRANSLATION],
  );
  const [translatedComment, setTranslatedComment] = useState(null);
  const [isTranslated, setIsTranslated] = useState(false);
  const { model } = useDynamicTranslationsProviderName();
  const { isPending, mutate } = useCreateCommentTranslation({
    onSuccess: (data) => {
      setIsTranslated(true);
      setTranslatedComment(data.translations[locale]);

      Steven.elementClicked({
        id: `dynamic_translations_${comment.commentable.__typename.toLowerCase()}_comment_translate_button`,
        content: 'activity.commentTranslate.translate',
        type: 'button',
        action: `translate_using_${model}_clicked`,
      });
    },
  });

  const handleTranslate = () => {
    if (translatedComment) {
      setIsTranslated(true);

      Steven.elementClicked({
        id: `dynamic_translations_${comment.commentable.__typename.toLowerCase()}_comment_translation_button`,
        content: 'activity.commentTranslate.translate',
        type: 'button',
        action: `see_translation_using_${model}_clicked`,
      });
    } else {
      const targetArrayWithoutDuplicates = [...new Set([locale, 'en'])];
      mutate({
        text: comment.comment,
        targets: targetArrayWithoutDuplicates,
        provider: model,
      });
    }
  };
  const handleSeeOriginal = () => {
    setIsTranslated(false);
    Steven.elementClicked({
      id: `dynamic_translations_${comment.commentable.__typename}_comment_see_original_button`,
      action: `see_original_using_${model}_clicked`,
      content: 'activity.commentTranslate.seeOriginal',
      type: 'button',
    });
  };
  // end of translation things

  const isSetRemindersEnabled = useFlags()[LD_SET_A_REMINDER_FLAG];

  const onClickReminderButton = () => {
    showModal(Modal.SET_A_REMINDER, {
      onRequestClose: () => hideModal(),
      reminderId: comment.databaseId,
      reminderType: 'comment',
    });
  };

  return (
    <StyledWrapper isEnabled={isSetRemindersEnabled}>
      <StyledButtonContainer className="comment-reminder">
        <ReminderButton onClick={onClickReminderButton} />
      </StyledButtonContainer>
      <div className={commentClasses}>
        <Avatar
          fullName={avatarData.name}
          imageSrc={avatarData.src}
          size="medium"
          borderColor={isDifferentCompany ? avatarData.accentColor : undefined}
        />
        <div className="comment-details">
          <div className="commentor-name">
            {isGuest ? <i className="mdi mdi-account-box guest" /> : null}
            <span>{creatorDisplayName}</span>
          </div>

          <CommentWrapper isPending={isPending}>
            <div className="commentor-comment">
              {isTranslated ? translatedComment : formattedComment}
              {!isCurrentUser && isDynamicTranslationEnabled ? (
                <CommentTranslateButton
                  handleTranslate={handleTranslate}
                  handleSeeOriginal={handleSeeOriginal}
                  language={locale}
                  originalLanguage={originalLanguage}
                  isTranslated={isTranslated}
                />
              ) : null}
            </div>
            {isPending ? <Loader /> : null}
          </CommentWrapper>
          {isGuest ? (
            <span className="comment-subnote">
              {translate('activity.comment.subnote')}
            </span>
          ) : null}
          <div className="comment-date-time">
            {formatCompactDateWithTime(comment.createdAt)}
          </div>
        </div>
      </div>
    </StyledWrapper>
  );
};

export default Comment;
